import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  SITE_ORIGIN,
  buildBreadcrumbJsonLd,
  buildNavigationJsonLd,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  resolveSeo,
} from '../../config/siteSeo';

const OG_IMAGE = `${SITE_ORIGIN}/img/sosvor-og.webp`;
const OG_IMAGE_WIDTH = '1200';
const OG_IMAGE_HEIGHT = '630';
const JSON_LD_ID = 'sosvor-jsonld';

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(graph) {
  let el = document.getElementById(JSON_LD_ID);
  if (!el) {
    el = document.createElement('script');
    el.id = JSON_LD_ID;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}

/**
 * Meta tags, Open Graph e JSON-LD por rota — melhora apresentação no Google.
 */
export default function PublicSeo() {
  const { pathname } = useLocation();
  const { locale } = useLanguage();

  const seo = useMemo(() => resolveSeo(pathname, locale), [pathname, locale]);

  useEffect(() => {
    const canonical = `${SITE_ORIGIN}${seo.path === '/' ? '/' : seo.path}`;
    const shortTitle = seo.title.includes('|') ? seo.title.split('|')[0].trim() : seo.title;

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'SOSVOR');
    upsertMeta('property', 'og:locale', locale === 'en' ? 'en_US' : locale === 'fr' ? 'fr_FR' : 'pt_AO');
    upsertMeta('property', 'og:title', shortTitle);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', OG_IMAGE);
    upsertMeta('property', 'og:image:width', OG_IMAGE_WIDTH);
    upsertMeta('property', 'og:image:height', OG_IMAGE_HEIGHT);
    upsertMeta('property', 'og:image:alt', 'SOSVOR — Tecnologia e Consultoria em Angola');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', shortTitle);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', OG_IMAGE);

    const graph = [
      buildOrganizationJsonLd(),
      buildWebSiteJsonLd(),
      buildNavigationJsonLd(),
    ];
    const breadcrumb = buildBreadcrumbJsonLd(pathname, shortTitle);
    if (breadcrumb) graph.push(breadcrumb);
    setJsonLd(graph);
  }, [seo, pathname, locale]);

  return null;
}
